// src/utils/scenarioSimulator.js
// Emergency scenario simulator for testing EyeOnStorm behavior.
// Import and call simulateScenario() in dev mode only.
// Never ships to production with active overrides.

import { useSituationStore } from '@store/situationStore';
import { useAlertStore }     from '@store/alertStore';
import { useIncidentStore }  from '@store/incidentStore';
import { useFeedStore }      from '@store/feedStore';

export const SCENARIOS = {
  normal: {
    name: 'Normal conditions',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [],
  },
  boilWater: {
    name: 'Boil water advisory (West Bank)',
    utilityStatus: {
      boilWaterActive: true,
      boilWaterZones: ['Algiers','Gretna','Westwego','Marrero','Harvey'],
      boilWaterParishes: ['Orleans','Jefferson'],
      boilWaterDetails: 'Precautionary boil water advisory in effect for West Bank',
      parishAffected: 0,
      fetchedAt: new Date().toISOString()
    },
    alerts: [],
    incidents: [],
  },
  majorOutage: {
    name: 'Major power outage',
    utilityStatus: { boilWaterActive: false, parishAffected: 15000, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [],
  },
  flashFlood: {
    name: 'Flash flood warning',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [{
      id: 'sim-flood-1', status: 'active', severity: 'severe',
      event: 'Flash Flood Warning', headline: 'Flash Flood Warning in effect',
      zones: [], parishes: ['Orleans'], updatedAt: new Date().toISOString()
    }],
    incidents: [],
  },
  hurricane: {
    name: 'Hurricane warning + road closures',
    utilityStatus: { boilWaterActive: false, parishAffected: 5000, fetchedAt: new Date().toISOString() },
    alerts: [{
      id: 'sim-hurr-1', status: 'active', severity: 'extreme',
      event: 'Hurricane Warning', headline: 'Hurricane Warning — Cat 3 approaching',
      zones: [], parishes: [], area: 'LA', updatedAt: new Date().toISOString()
    }],
    incidents: Array.from({length:8}, (_,i) => ({
      incidentId: `sim-road-${i}`, incidentId: `sim-road-${i}`, id: `sim-road-${i}`, source:'dotd_511la',
      StartLatitude: 29.95 + (Math.random()-0.5)*0.1,
      StartLongitude: -90.07 + (Math.random()-0.5)*0.1,
      title: ['I-10','I-610','US-90','LA-90'][i%4] + (i < 3 ? ' — Road Closed' : ' — Lane Closed'),
      description: i < 3 ? 'All lanes closed due to emergency' : '1 Right lane closed',
      RoadwayName: ['I-10','I-610','US-90','LA-90'][i%4],
      ingestedAt: new Date().toISOString()
    })),
  },
  fullStress: {
    name: 'Full stress — hurricane + flood + outage + boil water',
    utilityStatus: {
      boilWaterActive: true,
      boilWaterZones: ['Algiers','Gretna','Westwego','Marrero','Harvey','Mid-City','Lakeview'],
      boilWaterParishes: ['Orleans','Jefferson'],
      boilWaterDetails: 'Precautionary boil water advisory — do not use tap water',
      parishAffected: 25000,
      fetchedAt: new Date().toISOString()
    },
    alerts: [
      { id:'sim-hurr-1', status:'active', severity:'extreme',
        event:'Hurricane Warning', headline:'Hurricane Warning — Category 4 approaching SE Louisiana',
        zones:[], parishes:['Orleans','Jefferson','St. Bernard'], updatedAt: new Date().toISOString() },
      { id:'sim-flood-1', status:'active', severity:'severe',
        event:'Flash Flood Emergency', headline:'Flash Flood Emergency — life-threatening flooding',
        zones:[], parishes:['Orleans'], updatedAt: new Date().toISOString() },
      { id:'sim-surge-1', status:'active', severity:'extreme',
        event:'Storm Surge Warning', headline:'Dangerous storm surge 10-15 ft above ground',
        zones:[], parishes:['Orleans','Plaquemines'], updatedAt: new Date().toISOString() },
      { id:'sim-evac-1', status:'active', severity:'extreme',
        event:'Evacuation Order', headline:'Mandatory evacuation order — all zones',
        zones:[], parishes:['Orleans','Jefferson'], updatedAt: new Date().toISOString() },
    ],
    incidents: Array.from({length:15}, (_,i) => ({
      incidentId: `sim-road-${i}`, incidentId: `sim-road-${i}`, id: `sim-road-${i}`, source:'dotd_511la',
      StartLatitude: 29.95 + (Math.random()-0.5)*0.15,
      StartLongitude: -90.07 + (Math.random()-0.5)*0.15,
      title: ['I-10','I-610','US-90','LA-90','I-10E','Causeway'][i%6] + (i < 5 ? ' — Road Closed' : ' — Lane Closed'),
      description: i < 5 ? 'All lanes closed due to emergency' : i < 10 ? '2 Right lanes closed' : 'Road flooded — all lanes closed',
      RoadwayName: ['I-10','I-610','US-90','LA-90','I-10E','Causeway'][i%6],
      ingestedAt: new Date().toISOString()
    })),
  },

  // State transition tests
  hurricaneOnly: {
    name: 'Hurricane Warning only (no flood/surge/evac)',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [{ id:'sim-hurr-1', status:'active', severity:'extreme', simulated:true,
      event:'Hurricane Warning', headline:'🧪 SIMULATED — Hurricane Warning only',
      zones:[], parishes:[], area:'LA', updatedAt: new Date().toISOString() }],
    incidents: [],
  },
  nwsDown: {
    name: 'NWS unavailable — no alert data',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],  // empty — NWS down
    incidents: [],
  },
  dotdDownCachedClosures: {
    name: 'DOTD unavailable — 15 cached closures remain',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: Array.from({length:15}, (_,i) => ({
      incidentId: `sim-road-${i}`, incidentId: `sim-road-${i}`, id: `sim-road-${i}`, source:'dotd_511la',
      StartLatitude: 29.95 + (Math.random()-0.5)*0.05,
      StartLongitude: -90.07 + (Math.random()-0.5)*0.05,
      title: ['I-10','I-610','US-90','LA-90'][i%4] + (i < 3 ? ' — Road Closed' : ' — Lane Closed'),
      description: i < 3 ? 'All lanes closed due to emergency' : '1 Right lane closed',
      RoadwayName: ['I-10','I-610','US-90','LA-90'][i%4],
      ingestedAt: new Date(Date.now() - 45*60*1000).toISOString(), // 45min old = stale
    })),
  },
  dotdRestored: {
    name: 'DOTD restored — zero closures',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [], // DOTD back online, nothing found
  },
  expiredWarning: {
    name: 'Hurricane Warning expired',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [{ id:'sim-hurr-exp', status:'active', severity:'extreme', simulated:true,
      event:'Hurricane Warning', headline:'🧪 SIMULATED — Expired Hurricane Warning',
      zones:[], parishes:[], area:'LA',
      expiresAt: new Date(Date.now() - 60*1000).toISOString(), // expired 1min ago
      updatedAt: new Date().toISOString() }],
    incidents: [],
  },
  conflictingCommunity: {
    name: 'Conflicting community reports during closures',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [
      { id:'sim-road-0', source:'dotd_511la', Description:'All lanes closed',
        RoadwayName:'I-10', StartLatitude:29.95, StartLongitude:-90.07,
        ingestedAt: new Date().toISOString() },
      { id:'sim-comm-0', source:'community', type:'road_clear',
        title:'Roads look clear', description:'Community report: I-10 appears open',
        StartLatitude:29.95, StartLongitude:-90.07,
        ingestedAt: new Date().toISOString() },
    ],
  },

  // ── Adversarial tests ────────────────────────────────────────────
  malformedAlert: {
    name: 'Malformed NWS alert — missing required fields',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id:'sim-bad-1', simulated:true },
      { id:'sim-bad-2', status:'active', severity:null, event:undefined, simulated:true },
      { id:'sim-good-1', status:'active', severity:'severe', simulated:true,
        event:'Flash Flood Warning', headline:'🧪 SIMULATED — Flash Flood Warning',
        zones:[], parishes:[], area:'LA', updatedAt: new Date().toISOString() },
    ],
    incidents: [],
  },
  duplicateIncidents: {
    name: 'Duplicate DOTD incident IDs',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [
      { incidentId:'dup-1', id:'dup-1', source:'dotd_511la', status:'active',
        title:'I-10 — Road Closed', description:'All lanes closed due to accident',
        StartLatitude:29.95, StartLongitude:-90.07, ingestedAt: new Date().toISOString() },
      { incidentId:'dup-1', id:'dup-1', source:'dotd_511la', status:'active',
        title:'I-10 — Road Closed', description:'All lanes closed due to accident',
        StartLatitude:29.95, StartLongitude:-90.07, ingestedAt: new Date().toISOString() },
      { incidentId:'dup-1', id:'dup-1', source:'dotd_511la', status:'active',
        title:'I-10 — Road Closed', description:'All lanes closed due to accident',
        StartLatitude:29.95, StartLongitude:-90.07, ingestedAt: new Date().toISOString() },
    ],
  },
  badTimestamps: {
    name: 'Missing and invalid timestamps',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: 'not-a-date' },
    alerts: [
      { id:'sim-ts-1', status:'active', severity:'moderate', simulated:true,
        event:'Heat Advisory', headline:'🧪 SIMULATED — Heat Advisory',
        zones:[], parishes:[], area:'LA',
        updatedAt: "invalid-date", issuedAt: null, expiresAt: null },
    ],
    incidents: [
      { incidentId:'ts-1', id:'ts-1', source:'dotd_511la', status:'active',
        title:'LA-1 — Lane Closed', description:'1 Right lane closed',
        StartLatitude:29.95, StartLongitude:-90.07,
        ingestedAt: null, reportedAt: 'bad-date' },
    ],
  },
  futureDated: {
    name: 'Future-dated alert — should not activate yet',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id:'sim-future-1', status:'active', severity:'extreme', simulated:true,
        event:'Hurricane Warning', headline:'🧪 SIMULATED — Future Hurricane Warning',
        zones:[], parishes:[], area:'LA',
        issuedAt: new Date(Date.now() + 2*60*60*1000).toISOString(),
        updatedAt: new Date().toISOString() },
    ],
    incidents: [],
  },
  severityDecrease: {
    name: 'Warning severity decreases — EXTREME to MODERATE',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id:'sim-sev-1', status:'active', severity:'moderate', simulated:true,
        event:'Heat Advisory', headline:'🧪 SIMULATED — Downgraded to Heat Advisory',
        zones:[], parishes:[], area:'LA', updatedAt: new Date().toISOString() },
    ],
    incidents: [],
  },
  twoExtremeAlerts: {
    name: 'Two simultaneous EXTREME alerts',
    utilityStatus: { boilWaterActive: false, parishAffected: 5000, fetchedAt: new Date().toISOString() },
    alerts: [
      { id:'sim-ext-1', status:'active', severity:'extreme', simulated:true,
        event:'Hurricane Warning', headline:'🧪 SIMULATED — Hurricane Warning',
        zones:[], parishes:[], area:'LA', updatedAt: new Date().toISOString() },
      { id:'sim-ext-2', status:'active', severity:'extreme', simulated:true,
        event:'Storm Surge Warning', headline:'🧪 SIMULATED — Storm Surge Warning',
        zones:[], parishes:[], area:'LA', updatedAt: new Date().toISOString() },
    ],
    incidents: [],
  },
  manyIncidents: {
    name: '200 incidents — performance test',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: Array.from({length:200}, (_,i) => ({
      incidentId: `perf-${i}`, id: `perf-${i}`, source:'dotd_511la', status:'active',
      title: `${['I-10','I-610','US-90','LA-90','I-10E'][i%5]} — ${i%3===0?'Road Closed':'Lane Closed'}`,
      description: i%3===0 ? 'All lanes closed due to emergency' : '1 Right lane closed',
      StartLatitude: 29.95 + (Math.random()-0.5)*0.3,
      StartLongitude: -90.07 + (Math.random()-0.5)*0.3,
      ingestedAt: new Date(Date.now() - i*60*1000).toISOString(),
    })),
  },


  // ── Cross-screen regression scenario ─────────────────────────────
  // Fixed, deterministic state for asserting consistency across all screens.
  // Expected state on every screen:
  //   Alert: Hurricane Warning — EXTREME
  //   Roads: 3 closures (2 full, 1 lane)
  //   Power: 8,500 without power
  //   Source: NWS=live, DOTD=live, Entergy=live
  crossScreen: {
    name: "Cross-screen regression — Hurricane + Closures + Outages",
    utilityStatus: {
      boilWaterActive: false,
      parishAffected: 8500,
      fetchedAt: new Date().toISOString(),
    },
    alerts: [
      { id:"cs-alert-1", status:"active", severity:"severe", simulated:true,
        event:"Extreme Heat Warning", headline:"🧪 SIMULATED — Extreme Heat Warning",
        zones:[], parishes:[], area:"LA",
        issuedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 12*60*60*1000).toISOString() },
    ],
    incidents: [
      { incidentId:"cs-road-1", id:"cs-road-1", source:"dotd_511la", status:"active",
        title:"I-10 — Road Closed", description:"All lanes closed due to hurricane preparation",
        StartLatitude:29.95, StartLongitude:-90.07,
        ingestedAt: new Date().toISOString() },
      { incidentId:"cs-road-2", id:"cs-road-2", source:"dotd_511la", status:"active",
        title:"US-90 — Road Closed", description:"All lanes closed due to mandatory evacuation",
        StartLatitude:29.94, StartLongitude:-90.10,
        ingestedAt: new Date().toISOString() },
      { incidentId:"cs-road-3", id:"cs-road-3", source:"dotd_511la", status:"active",
        title:"LA-23 — Lane Closed", description:"1 Right lane closed — debris removal",
        StartLatitude:29.86, StartLongitude:-89.96,
        ingestedAt: new Date().toISOString() },
    ],
  },

  sosFeedStress: {
    name: 'SOS feed stress — 20 mixed community reports',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [],
    posts: [
      // 4 life-safety
      { id:'sim-sos-m1', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety', sosStatus:'needs_help',
        body:'I need immediate medical assistance. Chest pain, can not breathe.', zone:'St. Rose',
        lat:29.96, lon:-90.31, ts:new Date().toISOString(), createdAt:new Date().toISOString(), sourceType:'community', severity:5 },
      { id:'sim-sos-m2', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety', sosStatus:'needs_help',
        body:'Elderly neighbor unresponsive, need help now.', zone:'St. Rose',
        lat:29.961, lon:-90.312, ts:new Date(Date.now()-2*60000).toISOString(), createdAt:new Date(Date.now()-2*60000).toISOString(), sourceType:'community', severity:5 },
      { id:'sim-sos-t1', type:'sos', sosSubtype:'trapped', sosUrgency:'life_safety', sosStatus:'needs_help',
        body:'I am trapped and need rescue. Water rising in my house.', zone:'St. Rose',
        lat:29.959, lon:-90.309, ts:new Date(Date.now()-5*60000).toISOString(), createdAt:new Date(Date.now()-5*60000).toISOString(), sourceType:'community', severity:5 },
      { id:'sim-sos-t2', type:'sos', sosSubtype:'trapped', sosUrgency:'life_safety', sosStatus:'assistance_reported',
        body:'Trapped on roof, floodwater at door level.', zone:'St. Rose',
        lat:29.958, lon:-90.311, ts:new Date(Date.now()-12*60000).toISOString(), createdAt:new Date(Date.now()-12*60000).toISOString(), sourceType:'community', severity:5 },
      // 8 food/water
      ...Array.from({length:8}, (_,i) => ({
        id:`sim-sos-fw${i}`, type:'sos', sosSubtype:'food_water', sosUrgency:'urgent', sosStatus:'needs_help',
        body:'I need food and water. Have not eaten since yesterday.', zone:'St. Rose',
        lat:29.96+i*0.001, lon:-90.31-i*0.001,
        ts:new Date(Date.now()-(i+1)*8*60000).toISOString(),
        createdAt:new Date(Date.now()-(i+1)*8*60000).toISOString(),
        sourceType:'community', severity:3, simulated:true })),
      // 3 generator/power
      ...Array.from({length:3}, (_,i) => ({
        id:`sim-sos-g${i}`, type:'sos', sosSubtype:'generator', sosUrgency:'assistance', sosStatus:'needs_help',
        body:'Need generator assistance. Medical equipment requires power.', zone:'St. Rose',
        lat:29.957+i*0.002, lon:-90.308+i*0.001,
        ts:new Date(Date.now()-(i+2)*15*60000).toISOString(),
        createdAt:new Date(Date.now()-(i+2)*15*60000).toISOString(),
        sourceType:'community', severity:2, simulated:true })),
      // 3 welfare checks
      ...Array.from({length:3}, (_,i) => ({
        id:`sim-sos-w${i}`, type:'sos', sosSubtype:'welfare_check', sosUrgency:'urgent', sosStatus:'needs_help',
        body:'I need a welfare check. Have not been able to reach family.', zone:'St. Rose',
        lat:29.963+i*0.001, lon:-90.313-i*0.001,
        ts:new Date(Date.now()-(i+3)*20*60000).toISOString(),
        createdAt:new Date(Date.now()-(i+3)*20*60000).toISOString(),
        sourceType:'community', severity:3, simulated:true })),
      // 2 power reports
      { id:'sim-pow-1', type:'power', sourceType:'community',
        title:'Power out', body:'Power has been out on my block for 3 hours.', zone:'St. Rose',
        lat:29.962, lon:-90.310, ts:new Date(Date.now()-30*60000).toISOString(), createdAt:new Date(Date.now()-30*60000).toISOString(), severity:2 },
      { id:'sim-pow-2', type:'power', sourceType:'community',
        title:'Power restored', body:'Power just came back on my street.', zone:'St. Rose',
        lat:29.964, lon:-90.314, ts:new Date(Date.now()-10*60000).toISOString(), createdAt:new Date(Date.now()-10*60000).toISOString(), severity:1 },
    ],
  },


  // ── Community SOS intelligence adversarial tests ─────────────────

  sosUnrelatedToAlert: {
    name: 'SOS reports unrelated to active alert — should not inflate confidence',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id:'sim-heat-1', status:'active', severity:'moderate', simulated:true,
        event:'Heat Advisory', headline:'🧪 SIMULATED — Heat Advisory',
        zones:[], parishes:[], area:'LA', updatedAt: new Date().toISOString() },
    ],
    incidents: [],
    posts: [
      // Flood/rescue SOS — should NOT corroborate Heat Advisory
      { id:'sim-sos-flood1', type:'sos', sosSubtype:'trapped', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'Trapped by rising water, need rescue.',
        zone:'St. Rose', lat:29.96, lon:-90.31,
        ts:new Date().toISOString(), createdAt:new Date().toISOString(),
        sourceType:'community', severity:5, simulated:true },
      { id:'sim-sos-flood2', type:'sos', sosSubtype:'food_water', sosUrgency:'urgent',
        sosStatus:'needs_help', body:'Need food and water, roads flooded.',
        zone:'St. Rose', lat:29.961, lon:-90.312,
        ts:new Date().toISOString(), createdAt:new Date().toISOString(),
        sourceType:'community', severity:3, simulated:true },
      // Medical SOS — SHOULD corroborate Heat Advisory
      { id:'sim-sos-heat1', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'Elderly person collapsed, possible heat stroke.',
        zone:'St. Rose', lat:29.962, lon:-90.309,
        ts:new Date().toISOString(), createdAt:new Date().toISOString(),
        sourceType:'community', severity:5, simulated:true },
    ],
  },

  sosDuplicateCluster: {
    name: 'Duplicate SOS from same device/location — dedup test',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [],
    posts: [
      // Same coords, same subtype, 2 min apart — should be flagged as potential duplicate
      { id:'sim-dup-1', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'I need immediate medical assistance.',
        zone:'St. Rose', lat:29.960, lon:-90.310,
        ts:new Date().toISOString(), createdAt:new Date().toISOString(),
        sourceType:'community', severity:5, simulated:true },
      { id:'sim-dup-2', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'I need immediate medical assistance.',
        zone:'St. Rose', lat:29.960, lon:-90.310,
        ts:new Date(Date.now()-2*60000).toISOString(),
        createdAt:new Date(Date.now()-2*60000).toISOString(),
        sourceType:'community', severity:5, simulated:true },
      // Different coords, same subtype — should NOT be flagged as duplicate
      { id:'sim-dup-3', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'My neighbor needs medical help.',
        zone:'St. Rose', lat:29.965, lon:-90.318,
        ts:new Date(Date.now()-5*60000).toISOString(),
        createdAt:new Date(Date.now()-5*60000).toISOString(),
        sourceType:'community', severity:5, simulated:true },
    ],
  },

  sosConflictingPowerReports: {
    name: 'Conflicting power reports — outage then restoration nearby',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [],
    posts: [
      // Power out reported 45m ago
      { id:'sim-pow-out1', type:'power', sourceType:'community',
        title:'Power out', body:'Power has been out on Airline Hwy for 45 minutes.',
        zone:'St. Rose', lat:29.961, lon:-90.310,
        ts:new Date(Date.now()-45*60000).toISOString(),
        createdAt:new Date(Date.now()-45*60000).toISOString(),
        severity:2, simulated:true },
      // Power restored 10m ago — at same location
      { id:'sim-pow-rst1', type:'power', sourceType:'community',
        title:'Power restored', body:'Power just came back on Airline Hwy.',
        zone:'St. Rose', lat:29.961, lon:-90.310,
        ts:new Date(Date.now()-10*60000).toISOString(),
        createdAt:new Date(Date.now()-10*60000).toISOString(),
        severity:1, simulated:true },
      // Power still out at different location — both can be true
      { id:'sim-pow-out2', type:'power', sourceType:'community',
        title:'Power out', body:'Still no power on River Road.',
        zone:'St. Rose', lat:29.955, lon:-90.320,
        ts:new Date(Date.now()-20*60000).toISOString(),
        createdAt:new Date(Date.now()-20*60000).toISOString(),
        severity:2, simulated:true },
    ],
  },

  sosStaleReports: {
    name: 'Mix of fresh and stale SOS reports — freshness filter test',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [],
    posts: [
      // Fresh life-safety
      { id:'sim-fresh-1', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'Medical emergency right now.',
        zone:'St. Rose', lat:29.960, lon:-90.310,
        ts:new Date().toISOString(), createdAt:new Date().toISOString(),
        sourceType:'community', severity:5, simulated:true },
      // 6h old — should still show but marked stale
      { id:'sim-stale-1', type:'sos', sosSubtype:'food_water', sosUrgency:'urgent',
        sosStatus:'needs_help', body:'Need food and water. Submitted 6 hours ago.',
        zone:'St. Rose', lat:29.962, lon:-90.312,
        ts:new Date(Date.now()-6*3600000).toISOString(),
        createdAt:new Date(Date.now()-6*3600000).toISOString(),
        sourceType:'community', severity:3, simulated:true },
      // 25h old — should be pruned by pruneStaleContent
      { id:'sim-old-1', type:'sos', sosSubtype:'welfare_check', sosUrgency:'urgent',
        sosStatus:'needs_help', body:'Welfare check needed. Submitted yesterday.',
        zone:'St. Rose', lat:29.963, lon:-90.313,
        ts:new Date(Date.now()-25*3600000).toISOString(),
        createdAt:new Date(Date.now()-25*3600000).toISOString(),
        sourceType:'community', severity:3, simulated:true },
    ],
  },

  sosResolutionLifecycle: {
    name: 'SOS lifecycle — needs_help → assistance_reported → resolved',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [],
    incidents: [],
    posts: [
      // Active life-safety
      { id:'sim-life-1', type:'sos', sosSubtype:'trapped', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'Trapped and need rescue.',
        zone:'St. Rose', lat:29.960, lon:-90.310,
        ts:new Date(Date.now()-30*60000).toISOString(),
        createdAt:new Date(Date.now()-30*60000).toISOString(),
        sourceType:'community', severity:5, simulated:true },
      // Same incident — assistance reported
      { id:'sim-life-2', type:'sos', sosSubtype:'medical', sosUrgency:'life_safety',
        sosStatus:'assistance_reported',
        body:'Medical emergency — community responder en route.',
        zone:'St. Rose', lat:29.961, lon:-90.311,
        ts:new Date(Date.now()-15*60000).toISOString(),
        createdAt:new Date(Date.now()-15*60000).toISOString(),
        responseStatus:'community_responding',
        sourceType:'community', severity:5, simulated:true },
      // Resolved — reporter marked resolved
      { id:'sim-life-3', type:'sos', sosSubtype:'food_water', sosUrgency:'urgent',
        sosStatus:'resolved', sosResolved:true,
        sosResolvedAt:new Date(Date.now()-5*60000).toISOString(),
        body:'Food and water needed earlier — resolved.',
        zone:'St. Rose', lat:29.962, lon:-90.312,
        ts:new Date(Date.now()-60*60000).toISOString(),
        createdAt:new Date(Date.now()-60*60000).toISOString(),
        sourceType:'community', severity:3, simulated:true },
    ],
  },

  sosHundredReports: {
    name: '100 mixed SOS reports — rendering performance test',
    utilityStatus: { boilWaterActive: false, parishAffected: 2000, fetchedAt: new Date().toISOString() },
    alerts: [
      { id:'sim-hurr-100', status:'active', severity:'extreme', simulated:true,
        event:'Hurricane Warning', headline:'🧪 SIMULATED — Hurricane Warning',
        zones:[], parishes:[], area:'LA', updatedAt: new Date().toISOString() },
    ],
    incidents: [],
    posts: [
      // 8 life-safety
      ...Array.from({length:4}, (_,i) => ({
        id:`sim-100-med${i}`, type:'sos', sosSubtype:'medical', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'I need immediate medical assistance.',
        zone:'St. Rose', lat:29.96+i*0.001, lon:-90.31-i*0.001,
        ts:new Date(Date.now()-i*3*60000).toISOString(),
        createdAt:new Date(Date.now()-i*3*60000).toISOString(),
        sourceType:'community', severity:5, simulated:true })),
      ...Array.from({length:4}, (_,i) => ({
        id:`sim-100-trp${i}`, type:'sos', sosSubtype:'trapped', sosUrgency:'life_safety',
        sosStatus:'needs_help', body:'I am trapped and need rescue.',
        zone:'St. Rose', lat:29.957+i*0.001, lon:-90.308-i*0.001,
        ts:new Date(Date.now()-i*5*60000).toISOString(),
        createdAt:new Date(Date.now()-i*5*60000).toISOString(),
        sourceType:'community', severity:5, simulated:true })),
      // 40 food/water
      ...Array.from({length:40}, (_,i) => ({
        id:`sim-100-fw${i}`, type:'sos', sosSubtype:'food_water', sosUrgency:'urgent',
        sosStatus:'needs_help', body:'I need food and water.',
        zone:'St. Rose', lat:29.96+i*0.0005, lon:-90.31-i*0.0005,
        ts:new Date(Date.now()-i*10*60000).toISOString(),
        createdAt:new Date(Date.now()-i*10*60000).toISOString(),
        sourceType:'community', severity:3, simulated:true })),
      // 20 welfare checks
      ...Array.from({length:20}, (_,i) => ({
        id:`sim-100-wf${i}`, type:'sos', sosSubtype:'welfare_check', sosUrgency:'urgent',
        sosStatus:'needs_help', body:'I need a welfare check.',
        zone:'St. Rose', lat:29.963+i*0.0005, lon:-90.313-i*0.0005,
        ts:new Date(Date.now()-i*12*60000).toISOString(),
        createdAt:new Date(Date.now()-i*12*60000).toISOString(),
        sourceType:'community', severity:3, simulated:true })),
      // 15 generator
      ...Array.from({length:15}, (_,i) => ({
        id:`sim-100-gen${i}`, type:'sos', sosSubtype:'generator', sosUrgency:'assistance',
        sosStatus:'needs_help', body:'Need generator assistance.',
        zone:'St. Rose', lat:29.958+i*0.0005, lon:-90.315-i*0.0005,
        ts:new Date(Date.now()-i*15*60000).toISOString(),
        createdAt:new Date(Date.now()-i*15*60000).toISOString(),
        sourceType:'community', severity:2, simulated:true })),
      // 13 power/flood community reports
      ...Array.from({length:7}, (_,i) => ({
        id:`sim-100-pow${i}`, type:'power', sourceType:'community',
        title:'Power out', body:'No power on my street.',
        zone:'St. Rose', lat:29.961+i*0.001, lon:-90.311-i*0.001,
        ts:new Date(Date.now()-i*20*60000).toISOString(),
        createdAt:new Date(Date.now()-i*20*60000).toISOString(),
        severity:2, simulated:true })),
      ...Array.from({length:6}, (_,i) => ({
        id:`sim-100-fld${i}`, type:'flood', sourceType:'community',
        title:'Flooding', body:'Water rising on my street.',
        zone:'St. Rose', lat:29.959+i*0.001, lon:-90.309-i*0.001,
        ts:new Date(Date.now()-i*25*60000).toISOString(),
        createdAt:new Date(Date.now()-i*25*60000).toISOString(),
        severity:4, simulated:true })),
    ],
  },

  // ── IPAWS alert type tests ───────────────────────────────────────

  ipawsAmber: {
    name: 'IPAWS AMBER Alert — Missing Endangered Person',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id: 'ipaws_amber_test_1', source: 'ipaws', sourceType: 'official',
        status: 'active', msgType: 'Alert', simulated: true,
        event: 'Missing Endangered Persons',
        title: 'Missing Endangered Persons',
        category: 'Safety',
        severity: 'severe',
        urgency: 'Immediate',
        certainty: 'Observed',
        easOrg: 'CIV',
        headline: 'AMBER Alert — St. Charles Parish — 2026-08-22',
        description: 'LAC: AMBER ALERT. License: ABC1234. Black 2018 Toyota Camry. Missing: Marcus Johnson, 8yo male, 4ft tall, brown hair. Last seen Luling, LA. If seen call 911.',
        instruction: 'Do not attempt to apprehend. Call 911 immediately if you see this vehicle or child.',
        senderName: 'Louisiana State Police',
        areaDesc: 'St. Charles Parish, LA',
        parishes: ['St. Charles', 'Jefferson', 'St. John'],
        sameCodes: ['022089', '022051', '022095'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 12*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 12*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
        weaText: 'AMBER Alert: Black Toyota Camry LA/ABC1234. Call 911.',
      },
    ],
    incidents: [],
  },

  ipawsCivilEmergency: {
    name: 'IPAWS Civil Emergency Message — Hazmat',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id: 'ipaws_cdw_test_1', source: 'ipaws', sourceType: 'official',
        status: 'active', msgType: 'Alert', simulated: true,
        event: 'Civil Emergency Message',
        title: 'Civil Emergency Message',
        category: 'Safety',
        severity: 'severe',
        urgency: 'Immediate',
        certainty: 'Observed',
        easOrg: 'CIV',
        headline: 'Hazardous Materials Incident — St. Charles Parish',
        description: 'A chemical release has been reported at a facility in Norco, LA. Shelter in place immediately. Close all windows and doors. Turn off HVAC systems. Avoid the area of Airline Hwy between River Rd and Good Hope Rd.',
        instruction: 'SHELTER IN PLACE. Close all windows and doors. Turn off air conditioning and heating. Stay tuned for further instructions.',
        senderName: 'St. Charles Parish Office of Homeland Security',
        areaDesc: 'St. Charles Parish, LA',
        parishes: ['St. Charles'],
        sameCodes: ['022089'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 4*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 4*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
        weaText: 'HAZMAT INCIDENT: Shelter in place. Close windows. Avoid Norco area.',
      },
    ],
    incidents: [],
  },

  ipawsLocalEmergency: {
    name: 'IPAWS Local Parish Alert — not through NWS',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id: 'ipaws_local_test_1', source: 'ipaws', sourceType: 'official',
        status: 'active', msgType: 'Alert', simulated: true,
        event: 'Law Enforcement Warning',
        title: 'Law Enforcement Warning',
        category: 'Security',
        severity: 'severe',
        urgency: 'Immediate',
        certainty: 'Likely',
        easOrg: 'CIV',
        headline: 'Active Law Enforcement Operation — St. Rose Area',
        description: 'The St. Charles Parish Sheriff is conducting an active law enforcement operation in the St. Rose area. Residents on Airline Hwy between Veterans Blvd and Hwy 48 should stay indoors and away from windows until further notice.',
        instruction: 'Stay indoors. Stay away from windows. Do not interfere with law enforcement operations.',
        senderName: 'St. Charles Parish Sheriff',
        areaDesc: 'St. Rose, St. Charles Parish, LA',
        parishes: ['St. Charles'],
        sameCodes: ['022089'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 2*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 2*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
        weaText: 'LAW ENFORCEMENT: Stay indoors, away from windows. St. Rose area.',
      },
    ],
    incidents: [],
  },

  ipaws911Outage: {
    name: 'IPAWS 911 Outage Notification',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id: 'ipaws_911_test_1', source: 'ipaws', sourceType: 'official',
        status: 'active', msgType: 'Alert', simulated: true,
        event: '911 Telephone Outage Emergency',
        title: '911 Telephone Outage Emergency',
        category: 'Safety',
        severity: 'moderate',
        urgency: 'Immediate',
        certainty: 'Observed',
        easOrg: 'CIV',
        headline: '911 Outage — St. Charles Parish',
        description: 'The 911 system is currently experiencing an outage in St. Charles Parish. If you have an emergency, please call the St. Charles Parish Sheriff at (985) 783-6807 or the Luling Fire Department at (985) 785-1234.',
        instruction: 'Do not call 911 — lines are down. For emergencies call (985) 783-6807 (Sheriff) or (985) 785-1234 (Fire).',
        senderName: 'St. Charles Parish Office of Homeland Security',
        areaDesc: 'St. Charles Parish, LA',
        parishes: ['St. Charles'],
        sameCodes: ['022089'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 3*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 3*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
        weaText: '911 OUTAGE St. Charles Parish. Call (985) 783-6807 for emergencies.',
      },
    ],
    incidents: [],
  },

  ipawsBackupNWS: {
    name: 'IPAWS as NWS backup — Flash Flood Warning via IPAWS',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id: 'ipaws_nws_backup_1', source: 'ipaws', sourceType: 'official',
        status: 'active', msgType: 'Alert', simulated: true,
        event: 'Flash Flood Warning',
        title: 'Flash Flood Warning',
        category: 'Met',
        severity: 'severe',
        urgency: 'Immediate',
        certainty: 'Likely',
        easOrg: 'WXR',
        vtec: '/O.NEW.KLIX.FF.W.0001.260822T2000Z-260822T2359Z/',
        headline: 'Flash Flood Warning — St. Charles Parish until 7:59 PM CDT',
        description: 'The National Weather Service has issued a Flash Flood Warning for St. Charles Parish until 7:59 PM CDT. Heavy rain of 2-4 inches has fallen in the past hour. Avoid low-lying areas and do not drive through flooded roads.',
        instruction: 'Turn around, do not drown. Move to higher ground immediately if flooding occurs.',
        senderName: 'NWS New Orleans LA',
        areaDesc: 'St. Charles Parish, LA',
        parishes: ['St. Charles', 'Jefferson'],
        sameCodes: ['022089', '022051'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 2*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 2*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
        weaText: 'NWS: FLASH FLOOD WARNING this area til 7:59 PM CDT. Avoid flooded areas.',
      },
    ],
    incidents: [],
  },

  ipawsAllTypes: {
    name: 'IPAWS all alert types simultaneously',
    utilityStatus: { boilWaterActive: false, parishAffected: 0, fetchedAt: new Date().toISOString() },
    alerts: [
      { id: 'ipaws_combo_amber', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Missing Endangered Persons', title: 'Missing Endangered Persons',
        severity: 'severe', urgency: 'Immediate', certainty: 'Observed',
        easOrg: 'CIV',
        headline: 'AMBER Alert — Jefferson Parish',
        description: 'AMBER Alert: White 2020 Honda Civic LA/XYZ789. Missing child: Sofia Martinez, 6yo female.',
        instruction: 'Call 911 if seen. Do not approach.',
        senderName: 'Louisiana State Police',
        parishes: ['Jefferson', 'Orleans', 'St. Charles'],
        sameCodes: ['022051', '022071', '022089'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 12*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 12*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { id: 'ipaws_combo_cdw', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Civil Emergency Message', title: 'Civil Emergency Message',
        severity: 'severe', urgency: 'Immediate', certainty: 'Likely',
        easOrg: 'CIV',
        headline: 'Shelter in Place — Chemical Release — Norco',
        description: 'Shelter in place due to chemical release. St. Charles Parish.',
        instruction: 'Close windows and doors. Turn off HVAC.',
        senderName: 'St. Charles Parish OHS',
        parishes: ['St. Charles'],
        sameCodes: ['022089'],
        area: 'LA',
        issuedAt: new Date(Date.now()-5*60000).toISOString(),
        effective: new Date(Date.now()-5*60000).toISOString(),
        expires: new Date(Date.now() + 4*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 4*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { id: 'ipaws_combo_911', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: '911 Telephone Outage Emergency', title: '911 Telephone Outage Emergency',
        severity: 'moderate', urgency: 'Immediate', certainty: 'Observed',
        easOrg: 'CIV',
        headline: '911 Outage — St. Charles Parish',
        description: '911 system down in St. Charles Parish. Call (985) 783-6807.',
        instruction: 'Do not call 911. Use alternate numbers.',
        senderName: 'St. Charles Parish OHS',
        parishes: ['St. Charles'],
        sameCodes: ['022089'],
        area: 'LA',
        issuedAt: new Date(Date.now()-10*60000).toISOString(),
        effective: new Date(Date.now()-10*60000).toISOString(),
        expires: new Date(Date.now() + 3*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 3*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [],
  },

  // ── Cross-source reconciliation tests ───────────────────────────

  ipawsNWSDedup: {
    name: 'IPAWS + NWS same Tornado Warning — must show ONE alert',
    alerts: [
      // NWS path
      { id: 'nws_tor_w_001', source: 'nws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Tornado Warning', title: 'Tornado Warning',
        category: 'Met', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'WXR',
        vtec: '/O.NEW.KLIX.TO.W.0042.260822T2100Z-260822T2145Z/',
        headline: 'Tornado Warning — Jefferson Parish until 4:45 PM CDT',
        description: 'A tornado warning is in effect for Jefferson Parish.',
        instruction: 'Take shelter immediately in an interior room on the lowest floor.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Jefferson'], sameCodes: ['022051'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 45*60000).toISOString(),
        expiresAt: new Date(Date.now() + 45*60000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      // IPAWS path — same underlying warning, different ingestion
      { id: 'ipaws_tor_w_001', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Tornado Warning', title: 'Tornado Warning',
        category: 'Met', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'WXR',
        vtec: '/O.NEW.KLIX.TO.W.0042.260822T2100Z-260822T2145Z/',
        headline: 'Tornado Warning — Jefferson Parish until 4:45 PM CDT',
        description: 'A tornado warning is in effect for Jefferson Parish.',
        instruction: 'Take shelter immediately in an interior room on the lowest floor.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Jefferson'], sameCodes: ['022051'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 45*60000).toISOString(),
        expiresAt: new Date(Date.now() + 45*60000).toISOString(),
        updatedAt: new Date().toISOString(),
        // Dedup hint — same VTEC = same alert
        vtecMatch: '/O.NEW.KLIX.TO.W.0042.260822T2100Z-260822T2145Z/',
      },
    ],
    incidents: [],
    _test: {
      expect: 'Only ONE Tornado Warning shown — not two. Provenance shows both NWS + IPAWS paths.',
      check: 'Count alert cards on screen — should be 1, not 2.',
    },
  },

  ipawsUpdate: {
    name: 'IPAWS UPDATE references existing alert — mutate, not duplicate',
    alerts: [
      // Original alert (already ingested)
      { id: 'ipaws_ff_w_001', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Flash Flood Warning', title: 'Flash Flood Warning',
        category: 'Met', severity: 'severe', urgency: 'Immediate',
        certainty: 'Likely', easOrg: 'WXR',
        headline: 'Flash Flood Warning — St. Charles Parish until 6:00 PM CDT',
        description: 'Flash flooding ongoing in St. Charles Parish.',
        instruction: 'Turn around, do not drown.',
        senderName: 'NWS New Orleans LA',
        parishes: ['St. Charles'], sameCodes: ['022089'],
        area: 'LA', msgType: 'Alert',
        identifier: 'NWS-IDP-PROD-001',
        issuedAt: new Date(Date.now() - 30*60000).toISOString(),
        effective: new Date(Date.now() - 30*60000).toISOString(),
        expires: new Date(Date.now() + 90*60000).toISOString(),
        expiresAt: new Date(Date.now() + 90*60000).toISOString(),
        updatedAt: new Date(Date.now() - 30*60000).toISOString(),
      },
      // UPDATE — extends the warning, references original
      { id: 'ipaws_ff_w_001_upd', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Flash Flood Warning', title: 'Flash Flood Warning',
        category: 'Met', severity: 'severe', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'WXR',
        headline: 'Flash Flood Warning — St. Charles Parish EXTENDED until 8:00 PM CDT',
        description: 'Flash Flood Warning has been extended. Flooding is ongoing and expected to worsen.',
        instruction: 'Remain on higher ground. Do not attempt to cross flooded roads.',
        senderName: 'NWS New Orleans LA',
        parishes: ['St. Charles'], sameCodes: ['022089'],
        area: 'LA', msgType: 'Update',
        identifier: 'NWS-IDP-PROD-001-UPD',
        references: 'NWS-IDP-PROD-001',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 3*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 3*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [],
    _test: {
      expect: 'ONE Flash Flood Warning shown with updated expiry and EXTENDED headline. Not two cards.',
      check: 'Alert shows updated headline. Original 6 PM expiry replaced by 8 PM.',
    },
  },

  ipawsCancel: {
    name: 'IPAWS CANCEL — alert transitions to inactive immediately',
    alerts: [
      // The cancellation — references original alert
      { id: 'ipaws_ff_cancel', source: 'ipaws', sourceType: 'official',
        status: 'cancelled', simulated: true,
        event: 'Flash Flood Warning', title: 'Flash Flood Warning',
        category: 'Met', severity: 'severe', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'WXR',
        headline: 'Flash Flood Warning CANCELLED — St. Charles Parish',
        description: 'The Flash Flood Warning for St. Charles Parish has been cancelled.',
        instruction: 'Flooding threat has passed. Continue to exercise caution in low-lying areas.',
        senderName: 'NWS New Orleans LA',
        parishes: ['St. Charles'], sameCodes: ['022089'],
        area: 'LA', msgType: 'Cancel',
        identifier: 'NWS-IDP-PROD-001-CAN',
        references: 'NWS-IDP-PROD-001',
        issuedAt: new Date().toISOString(),
        expires: new Date(Date.now() - 1000).toISOString(),
        expiresAt: new Date(Date.now() - 1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [],
    _test: {
      expect: 'NO active Flash Flood Warning shown. Cancelled alert does not appear in active list.',
      check: 'Situation summary shows No alerts for Weather row.',
    },
  },

  ipawsMEPplusTornado: {
    name: 'AMBER Alert + Tornado Warning simultaneously — Tornado dominates protective action',
    alerts: [
      { id: 'ipaws_mep_sim', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Missing Endangered Persons', title: 'Missing Endangered Persons',
        category: 'Safety', severity: 'severe', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'CIV',
        headline: 'AMBER Alert — Jefferson Parish',
        description: 'AMBER Alert: White 2020 Honda Civic LA/XYZ789.',
        instruction: 'Do not attempt to apprehend. Call 911 if seen.',
        senderName: 'Louisiana State Police',
        parishes: ['Orleans','Jefferson','St. Bernard','Plaquemines','St. Charles','St. John','St. Tammany','Tangipahoa','Lafourche','Terrebonne','Assumption','Iberville','West Baton Rouge','Ascension','Livingston'],
        sameCodes: ['022071','022051','022087','022075','022089','022095','022103','022105','022057','022109','022007','022047','022121','022005','022063'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 12*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 12*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { id: 'nws_tor_mep_sim', source: 'nws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Tornado Warning', title: 'Tornado Warning',
        category: 'Met', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'WXR',
        headline: 'Tornado Warning — Jefferson Parish until 4:45 PM CDT',
        description: 'Tornado on the ground moving northeast at 35 mph.',
        instruction: 'Take shelter immediately in an interior room on the lowest floor. Do not wait.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Jefferson'], sameCodes: ['022051'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 30*60000).toISOString(),
        expiresAt: new Date(Date.now() + 30*60000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [],
    _test: {
      expect: 'Tornado Warning dominates primary recommendation (take shelter). AMBER Alert visible but secondary. Both shown in situation summary.',
      check: 'WhatShouldIDo shows tornado shelter instruction. Public Safety row shows AMBER. Weather row shows Tornado Warning.',
    },
  },

  ipawsEvacuationCombo: {
    name: 'Evacuation + Hurricane + DOTD closures — three sources, one coherent situation',
    alerts: [
      { id: 'ipaws_evac_sim', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Evacuation Immediate', title: 'Evacuation Order',
        category: 'Safety', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'CIV',
        headline: 'Mandatory Evacuation — St. Charles Parish — Zones A and B',
        description: 'The St. Charles Parish President has ordered mandatory evacuation of all residents in Zones A and B effective immediately.',
        instruction: 'Leave immediately via LA-3127 north to I-10 east. Do not take LA-18. Contraflow is in effect on I-10.',
        senderName: 'St. Charles Parish President',
        parishes: ['St. Charles'], sameCodes: ['022089'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 24*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 24*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { id: 'nws_hur_sim', source: 'nws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Hurricane Warning', title: 'Hurricane Warning',
        category: 'Met', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Likely', easOrg: 'WXR',
        headline: 'Hurricane Warning — Southeast Louisiana',
        description: 'Hurricane conditions expected within 36 hours. Category 3 storm approaching.',
        instruction: 'Complete all preparations and evacuate if ordered. Hurricane-force winds will make travel impossible once storm arrives.',
        senderName: 'NWS New Orleans LA',
        parishes: ['St. Charles', 'Jefferson', 'Orleans', 'St. Bernard', 'Plaquemines'],
        sameCodes: ['022089', '022051', '022071', '022087', '022075'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 36*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 36*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [
      { id: 'dotd_contraflow_sim', type: 'road_closure', simulated: true,
        title: 'Contraflow I-10 eastbound', severity: 'high',
        location: 'I-10 from Boutte to New Orleans', zone: 'St. Charles',
        description: 'Contraflow operation in effect. All lanes carry outbound traffic.',
        ts: Date.now(), lat: 29.9, lon: -90.35,
      },
    ],
    _test: {
      expect: 'Evacuation Order dominates recommendation. Hurricane Warning in Weather row. Road closure in Roads row. All three sources visible without contradiction.',
      check: 'WhatShouldIDo shows evacuation instruction from St. Charles Parish. Situation has 3 non-green rows simultaneously.',
    },
  },

  ipawsUnknownEventType: {
    name: 'Unknown IPAWS event type — safe fallback, no invented guidance',
    alerts: [
      { id: 'ipaws_unknown_sim', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Special Marine Warning',
        title: 'Special Marine Warning',
        category: 'Met', severity: 'moderate', urgency: 'Expected',
        certainty: 'Likely', easOrg: 'WXR',
        headline: 'Special Marine Warning — Lake Pontchartrain',
        description: 'Thunderstorms producing waterspouts and winds to 50 knots expected over Lake Pontchartrain through 8 PM CDT.',
        instruction: 'Mariners should seek safe harbor immediately.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Orleans', 'St. Tammany'],
        sameCodes: ['022071', '022103'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 2*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 2*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [],
    _test: {
      expect: 'Alert displays safely with original headline and official instruction. Classified as Weather (WXR). No invented guidance.',
      check: 'Alert appears. WhatShouldIDo does not invent marine-specific instructions.',
    },
  },

  ipawsTornadoFlood: {
    name: 'Tornado Warning + Flash Flood Warning + IPAWS duplicate — hardest arbitration test',
    alerts: [
      // Tornado Warning via NWS
      { id: 'nws_tor_tf_001', source: 'nws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Tornado Warning', title: 'Tornado Warning',
        category: 'Met', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'WXR',
        vtec: '/O.NEW.KLIX.TO.W.0043.260822T2200Z-260822T2245Z/',
        headline: 'Tornado Warning — Jefferson Parish until 5:45 PM CDT',
        description: 'A tornado warning is in effect. Tornado on the ground moving NE at 40 mph.',
        instruction: 'Take shelter immediately in an interior room on the lowest floor. Do not wait.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Orleans','Jefferson','St. Bernard','Plaquemines','St. Charles','St. John','St. Tammany','Tangipahoa'],
        sameCodes: ['022071','022051','022087','022075','022089','022095','022103','022105'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 30*60000).toISOString(),
        expiresAt: new Date(Date.now() + 30*60000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      // Same Tornado Warning via IPAWS — dedup candidate
      { id: 'ipaws_tor_tf_001', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Tornado Warning', title: 'Tornado Warning',
        category: 'Met', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'WXR',
        vtec: '/O.NEW.KLIX.TO.W.0043.260822T2200Z-260822T2245Z/',
        headline: 'Tornado Warning — Jefferson Parish until 5:45 PM CDT',
        description: 'A tornado warning is in effect. Tornado on the ground moving NE at 40 mph.',
        instruction: 'Take shelter immediately in an interior room on the lowest floor. Do not wait.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Orleans','Jefferson','St. Bernard','Plaquemines','St. Charles','St. John','St. Tammany','Tangipahoa'],
        sameCodes: ['022071','022051','022087','022075','022089','022095','022103','022105'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 30*60000).toISOString(),
        expiresAt: new Date(Date.now() + 30*60000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      // Flash Flood Warning via NWS — separate hazard
      { id: 'nws_ffw_tf_001', source: 'nws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Flash Flood Warning', title: 'Flash Flood Warning',
        category: 'Met', severity: 'severe', urgency: 'Immediate',
        certainty: 'Likely', easOrg: 'WXR',
        vtec: '/O.NEW.KLIX.FF.W.0022.260822T2200Z-260822T2359Z/',
        headline: 'Flash Flood Warning — Jefferson and Orleans Parishes until 7:59 PM CDT',
        description: 'Flash flooding ongoing. 3 inches of rain in the past hour.',
        instruction: 'Turn around, do not drown. Move to higher ground immediately.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Orleans','Jefferson','St. Bernard','Plaquemines','St. Charles','St. John','St. Tammany','Tangipahoa','Lafourche','Terrebonne','Assumption','Iberville','West Baton Rouge','Ascension','Livingston'],
        sameCodes: ['022071','022051','022087','022075','022089','022095','022103','022105','022057','022109','022007','022047','022121','022005','022063'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 2*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 2*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [],
    _test: {
      expect: [
        'TWO distinct alerts shown (Tornado Warning + Flash Flood Warning) — not three.',
        'Tornado Warning dominates recommendation (take shelter, extreme priority).',
        'Flash Flood Warning visible but secondary.',
        'Tornado actions contain ZERO flood language.',
        'Flash Flood actions contain ZERO tornado language.',
        'No shelter availability shown for either hazard.',
      ],
      arbitration: 'Tornado Warning wins primary recommendation because: extreme severity + Immediate urgency + Observed certainty + shorter window (30min vs 2hr).',
    },
  },


  ipawsRouteConflict: {
    name: 'Evacuation route × DOTD closure — official sources in tension',
    alerts: [
      { id: 'ipaws_evac_route', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Evacuation Immediate', title: 'Evacuation Order',
        category: 'Safety', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'CIV',
        headline: 'Mandatory Evacuation — St. Charles Parish — Zones A and B',
        description: 'The St. Charles Parish President has ordered mandatory evacuation of all residents in Zones A and B effective immediately.',
        instruction: 'Leave immediately via LA-3127 north to I-10 east. Do not take LA-18. Contraflow is in effect on I-10.',
        senderName: 'St. Charles Parish President',
        parishes: ['Orleans','Jefferson','St. Bernard','Plaquemines','St. Charles','St. John','St. Tammany','Tangipahoa','Lafourche','Terrebonne'],
        sameCodes: ['022071','022051','022087','022075','022089','022095','022103','022105','022057','022109'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 24*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 24*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { id: 'nws_hur_route', source: 'nws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Hurricane Warning', title: 'Hurricane Warning',
        category: 'Met', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Likely', easOrg: 'WXR',
        headline: 'Hurricane Warning — Southeast Louisiana',
        description: 'Category 3 hurricane approaching. Landfall expected within 24 hours.',
        instruction: 'Complete preparations and evacuate if ordered.',
        senderName: 'NWS New Orleans LA',
        parishes: ['Orleans','Jefferson','St. Bernard','Plaquemines','St. Charles','St. John','St. Tammany'],
        sameCodes: ['022071','022051','022087','022075','022089','022095','022103'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 36*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 36*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    incidents: [],
    posts: [
      // Road closure directly on the instructed evacuation route — sim- prefix lands in zoneFeed
      { id: 'sim-dotd_la3127_closure', type: 'road_closure', simulated: true,
        source: 'dotd', sourceType: 'dotd',
        title: 'LA-3127 CLOSED — Accident with injuries',
        severity: 'high',
        location: 'LA-3127 northbound at Hwy 90 intersection, Boutte',
        zone: 'St. Charles',
        body: 'LA-3127 northbound is closed at the Hwy 90 intersection due to a multi-vehicle accident.',
        road: 'LA-3127',
        direction: 'northbound',
        ts: Date.now(),
        lat: 29.888, lon: -90.377,
        raw: { updatedAt: new Date().toISOString() },
      },
    ],
    _test: {
      expect: [
        'Evacuation order dominates recommendation.',
        'Route conflict detected: official instruction says LA-3127; DOTD closure is on LA-3127.',
        'EyeOnStorm does NOT invent alternate route.',
        'Warning shown: "Road closure may affect the official evacuation route."',
        'Follow authority instruction: "Follow updated instructions from St. Charles Parish officials."',
        'View road closure link shown.',
      ],
    },
  },


  ipawsCancellation: {
    name: 'IPAWS evacuation order cancelled mid-event',
    description: 'Tests that a cancelled IPAWS alert is removed from the UI within one poll cycle.',
    alerts: [
      // Step 1: Active evacuation order (shown first)
      { id: 'ipaws_evac_cancel_test', source: 'ipaws', sourceType: 'official',
        status: 'active', simulated: true,
        event: 'Evacuation Immediate', title: 'Evacuation Order',
        category: 'Safety', severity: 'extreme', urgency: 'Immediate',
        certainty: 'Observed', easOrg: 'CIV',
        headline: 'Mandatory Evacuation — St. Charles Parish — Zones A and B',
        description: 'The St. Charles Parish President has ordered mandatory evacuation of all residents in Zones A and B effective immediately.',
        instruction: 'Leave immediately via LA-3127 north to I-10 east.',
        senderName: 'St. Charles Parish President',
        originator: 'St. Charles Parish President',
        authorityType: 'OFFICIAL',
        deliveryPaths: ['FEMA_IPAWS'],
        parishes: ['St. Charles','Jefferson','Orleans'],
        area: 'LA',
        issuedAt: new Date().toISOString(),
        effective: new Date().toISOString(),
        expires: new Date(Date.now() + 24*3600*1000).toISOString(),
        expiresAt: new Date(Date.now() + 24*3600*1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    // Cancellation alert — simulates what arrives after parish lifts order
    cancellationAlerts: [
      { id: 'ipaws_evac_cancel_test', source: 'ipaws', sourceType: 'official',
        status: 'cancelled', simulated: true,
        event: 'Evacuation Immediate',
        cancelledAt: new Date().toISOString(),
        expiresAt: new Date().toISOString(), // expired immediately
        senderName: 'St. Charles Parish President',
        description: 'The mandatory evacuation order for St. Charles Parish Zones A and B has been lifted. Residents may return when conditions are safe.',
      },
    ],
    incidents: [],
    posts: [],
    _test: {
      steps: [
        'Step 1: Run window.sim("ipawsCancellation", true) — evacuation order appears as EXTREME alert.',
        'Step 2: Run window.sim("ipawsCancellationCancel", true) — evacuation order disappears from feed.',
        'Step 3: Verify no "all clear" language — alert simply stops showing.',
        'Step 4: Verify health score returns to normal baseline.',
      ],
      expect: [
        'Evacuation order shown as EXTREME after Step 1.',
        'Evacuation order absent from feed after Step 2.',
        'No false "all clear" message displayed.',
        'WhatShouldIDo returns to default guidance after cancellation.',
      ],
    },
  },

  ipawsCancellationCancel: {
    name: 'IPAWS cancellation applied — clears evacuation order',
    alerts: [], // no active alerts — cancellation removes the evacuation
    cancellationAlerts: [
      { id: 'ipaws_evac_cancel_test', source: 'ipaws', sourceType: 'official',
        status: 'cancelled', simulated: true,
        event: 'Evacuation Immediate',
        cancelledAt: new Date().toISOString(),
        expiresAt: new Date().toISOString(),
        senderName: 'St. Charles Parish President',
        description: 'Evacuation order lifted.',
      },
    ],
    incidents: [],
    posts: [],
    _test: {
      expect: [
        'No EXTREME alerts shown.',
        'WhatShouldIDo shows default "stay prepared" guidance.',
        'No residual evacuation action in recommendation panel.',
      ],
    },
  },

  dataUnavailable: {
    name: 'DOTD unavailable',
    utilityStatus: null,
    alerts: [],
    incidents: [],
  },
};

export function simulateScenario(scenarioKey, force = false) {
  if (import.meta.env.PROD && !force) {
    console.warn('[Simulator] Disabled in production. Pass true as second arg to force: window.sim("hurricane", true)');
    return;
  }
  const scenario = SCENARIOS[scenarioKey];
  if (!scenario) { console.warn('[Simulator] Unknown scenario:', scenarioKey); return; }

  const store = useSituationStore.getState();
  if (scenario.utilityStatus !== undefined) store.setUtilityStatus(scenario.utilityStatus);
  if (scenario.incidents)                   store.setIncidents(scenario.incidents);
  if (scenario.alerts)                      store.setAlerts(scenario.alerts);

  // Reset stores — clear any corrupted simulator state first
  useIncidentStore.getState().setIncidents([]);  // reset so Firestore can repopulate
  // Set alerts — triggers useAlerts subscriptions
  useAlertStore.getState().setAlerts(scenario.alerts ?? []);
  // Set simulated incidents (empty for normal scenario restores Firestore data)
  if ((scenario.incidents ?? []).length > 0) {
    useIncidentStore.getState().setIncidents(scenario.incidents);
  }
  console.info('[Simulator] Alert store now has:', useAlertStore.getState().nwsAlerts.map(a => a.event));

  // Override WhatShouldIDo by setting window flag
  window.__simScenario = scenarioKey;
  // Inject community posts if scenario defines them
  if (scenario.posts?.length > 0) {
    const feedStore = useFeedStore.getState();
    // Inject directly to posts only — NOT userPosts (sim posts aren't "own" posts)
    feedStore.setPosts?.([...scenario.posts, ...feedStore.posts.filter(p => !p.id?.startsWith('sim-'))]);
    console.info('[Simulator] Injected', scenario.posts.length, 'community posts');
  }
  console.info('[Simulator] Applied scenario:', scenario.name);
  // Force React re-render by dispatching storage event (triggers Zustand subscriptions)
  window.dispatchEvent(new Event('storage'));
  // Also trigger a small delay then dispatch again
  setTimeout(() => window.dispatchEvent(new Event('storage')), 500);
  console.info('[Simulator] Tip: navigate to each screen to see stress test results');
}

// Always expose window.sim — requires force=true on production
window.sim = simulateScenario;
if (import.meta.env.DEV) {
  console.info('[Simulator] Basic: window.sim("normal"|"hurricane"|"fullStress"|"flashFlood"|"boilWater"|"majorOutage"|"dataUnavailable")');
  console.info('[Simulator] Transition tests: window.sim("hurricaneOnly"|"nwsDown"|"dotdDownCachedClosures"|"dotdRestored"|"expiredWarning"|"conflictingCommunity")');
  console.info('[Simulator] Adversarial tests: window.sim("malformedAlert"|"duplicateIncidents"|"badTimestamps"|"futureDated"|"severityDecrease"|"twoExtremeAlerts"|"manyIncidents")');
  console.info('[Simulator] Cancellation tests: (1) window.sim("ipawsCancellation", true) → shows evacuation order  (2) window.sim("ipawsCancellationCancel", true) → clears it');
} else {
  console.info('[Simulator] window.sim available — pass true as second arg on production: window.sim("fullStress", true)');
}
